"use client";

import { c as _c } from "react/compiler-runtime";
import { useRef } from "react";
import { motion, useScroll, useTransform, type MotionValue } from "framer-motion";
import { Check } from "lucide-react";
import { Card } from "@/shared/components/ui/card";
import { SHOWCASE_BLOCKS, type ShowcaseBlock } from "../data/features";
import { ShowcaseMock } from "./showcase-mocks";
import { Reveal } from "./shared/reveal";
const TOTAL = SHOWCASE_BLOCKS.length;

// One stacking card: pins to the viewport and scales down as the next card
// scrolls up and covers it, so only one card is "front" at a time.
const StackCard = t0 => {
  const $ = _c(34);
  const {
    block,
    index,
    progress
  } = t0;
  const targetScale = 1 - (TOTAL - 1 - index) * 0.04;
  const t1 = index / TOTAL;
  let t2;
  if ($[0] !== t1) {
    t2 = [t1, 1];
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== targetScale) {
    t3 = [1, targetScale];
    $[2] = targetScale;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const scale = useTransform(progress, t2, t3);
  const t4 = index + 1;
  let t5;
  if ($[4] !== t4) {
    t5 = <span className="font-mono text-sm text-zinc-400">0{t4}</span>;
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== block.eyebrow) {
    t6 = <span className="text-sm font-semibold uppercase tracking-widest text-zinc-500">{block.eyebrow}</span>;
    $[6] = block.eyebrow;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t5 || $[9] !== t6) {
    t7 = <div className="flex items-center gap-3">{t5}{t6}</div>;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== block.title) {
    t8 = <h3 className="mt-4 text-2xl font-extrabold tracking-tight sm:text-3xl">{block.title}</h3>;
    $[11] = block.title;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== block.description) {
    t9 = <p className="mt-4 text-pretty text-muted-foreground">{block.description}</p>;
    $[13] = block.description;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== block.bullets) {
    t10 = block.bullets.map(_temp);
    $[15] = block.bullets;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== t10) {
    t11 = <ul className="mt-6 space-y-3">{t10}</ul>;
    $[17] = t10;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== t11 || $[20] !== t7 || $[21] !== t8 || $[22] !== t9) {
    t12 = <div>{t7}{t8}{t9}{t11}</div>;
    $[19] = t11;
    $[20] = t7;
    $[21] = t8;
    $[22] = t9;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== block.id) {
    t13 = <div className="relative hidden lg:block"><ShowcaseMock id={block.id} /></div>;
    $[24] = block.id;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== t12 || $[27] !== t13) {
    t14 = <Card className="overflow-hidden border-border shadow-2xl"><div className="grid gap-8 p-6 sm:p-10 lg:grid-cols-2 lg:items-center">{t12}{t13}</div></Card>;
    $[26] = t12;
    $[27] = t13;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  const inner = t14;
  let t15;
  if ($[29] !== scale) {
    t15 = {
      scale
    };
    $[29] = scale;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== inner || $[32] !== t15) {
    t16 = <div className="sticky top-0 flex min-h-[60vh] items-center justify-center px-4 py-8 lg:min-h-screen lg:py-16"><motion.div style={t15} className="w-full max-w-5xl origin-top">{inner}</motion.div></div>;
    $[31] = inner;
    $[32] = t15;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  return t16;
};
export const FeatureShowcase = () => {
  const $ = _c(6);
  const containerRef = useRef(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      target: containerRef,
      offset: ["start start", "end end"]
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    scrollYProgress
  } = useScroll(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Reveal className="mx-auto max-w-2xl px-4 text-center sm:px-6 lg:px-8"><span className="text-sm font-semibold uppercase tracking-widest text-zinc-500">Product tour</span><h2 className="mt-3 text-balance text-3xl font-extrabold tracking-tight sm:text-4xl">Everything your gym runs on, in one app</h2><p className="mt-4 text-pretty text-muted-foreground">Members, billing, attendance, payroll, point of sale and real financials. See how each piece works, and how they come together.</p></Reveal>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== scrollYProgress) {
    t2 = SHOWCASE_BLOCKS.map((block, i) => <StackCard key={block.id} block={block} index={i} progress={scrollYProgress} />);
    $[2] = scrollYProgress;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <section id="showcase" className="relative py-20 sm:py-28">{t1}<div ref={containerRef} className="relative mt-12">{t2}</div></section>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
};
function _temp(bullet) {
  return <li key={bullet} className="flex items-start gap-3"><span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary"><Check className="size-3" /></span><span className="text-sm">{bullet}</span></li>;
}